import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Loader } from "Components/General/Loader";
import { BasicTab } from "../Components/BasicTab";
import { Tabs, Button, Row, message, Checkbox, Col, FormInstance, Form } from "antd";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import { I18N_NS } from "Config/i18n";
import { projectSubmitStore } from "Pages/Admin/Stores/ProjectStore";
import { ModulesTab } from "../Components/ModulesTab";
import { FunctionsTab } from "../Components/FunctionsTab";
import { OptionsTab } from "../Components/OptionsTab";
import { BillingTab } from "../Components/BillingTab";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { projectsDataStore } from "Stores";
import classnames from "classnames";
import { TimersTab } from "../Components/TimersTab";
import { UsersTab } from "../Components/UsersTab";
import { ProjectUsersStore } from "Pages/Admin/Stores/ProjectUsersStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InformationTab } from "../Components/InformationTab";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";

interface ProjectViewProps extends WithRouterProps, WithTranslation {
  isEditMode: boolean;
}

interface ProjectViewState {
  activeKey: string;
  continueEditing: boolean;
  folderStructure: API_TreeElement | Array<API_TreeElement>;
}

interface TabProps {
  label: string;
  component: React.ReactNode;
  disabled: boolean;
}

export const ProjectForm = withTranslation([I18N_NS.PROJECT, I18N_NS.GENERAL])(
  withRouter(
    observer(
      class ProjectView extends React.Component<ProjectViewProps, ProjectViewState> {
        constructor(props: ProjectViewProps) {
          super(props);
          this.state = {
            activeKey: "Basics",
            continueEditing: true,
            folderStructure: null,
          };
        }
        formRef: React.RefObject<FormInstance> = React.createRef();
        private readonly projectUsersStore = new ProjectUsersStore();

        componentDidMount() {
          if (this.props.params.id) {
            const { t } = this.props;
            (async () => {
              try {
                await projectSubmitStore.fetch(+this.props.params.id);
                await this.projectUsersStore.fetch(+this.props.params.id);
                projectsDataStore.setProjectId(+this.props.params.id);
                await projectSubmitStore.fetchInfo(+this.props.params.id);

                if (!groupListStore.list.length) {
                  await groupListStore.fetch({ filter: "rights" });
                }
              } catch (err) {
                this.props.navigate("/admin/projects");
                notificationError(t("Error"), t(`${I18N_NS.GENERAL}:::Something technical went wrong.`));
                throw err;
              }
            })();
          }
        }

        componentWillUnmount() {
          projectSubmitStore.clear();
          this.projectUsersStore.clear();
          projectsDataStore.resetProjectId();
          groupListStore.reset();
        }

        handleBack = () => {
          this.props.navigate("/admin/projects", { state: { searchQuery: this.props.location.state?.searchQuery } });
        };

        get subPages() {
          const { t } = this.props;
          const subPagesConfig: Array<TabProps> = [
            {
              label: "Basics",
              component: (
                <BasicTab
                  setFolderStructure={this.setFolderStructure}
                  isEditView={this.props.isEditMode}
                  formRef={this.formRef}
                  data={projectSubmitStore.data}
                  fetchPresets={projectSubmitStore.getPresets}
                />
              ),
              disabled: false,
            },
            {
              label: "Timers",
              component: (
                <TimersTab
                  status={projectSubmitStore.data.status}
                  scheduledJobs={projectSubmitStore.data.scheduledJobs}
                  isEditView={this.props.isEditMode}
                  formRef={this.formRef}
                />
              ),
              disabled: !this.props.isEditMode,
            },
            {
              label: "Billing",
              component: (
                <BillingTab
                  billingInformation={projectSubmitStore.billing}
                  isEditView={this.props.isEditMode}
                  status={projectSubmitStore.data.status}
                />
              ),
              disabled: !this.props.isEditMode,
            },
            {
              label: "Modules",
              component: (
                <ModulesTab
                  hasModules={projectSubmitStore.hasModules}
                  modulesActive={projectSubmitStore.modules}
                  startWith={projectSubmitStore.data.startWith}
                  isEditView={this.props.isEditMode}
                  formRef={this.formRef}
                />
              ),
              disabled: !this.props.isEditMode,
            },
            {
              label: "Functions",
              component: (
                <FunctionsTab
                  modulesActive={projectSubmitStore.modules}
                  functionsActive={projectSubmitStore.functions}
                  functionsOptions={projectSubmitStore.functionsOptions}
                  hasFunctions={projectSubmitStore.hasFunctions}
                  loginmode={projectSubmitStore.data.loginMode}
                  formRef={this.formRef}
                  isEditView={this.props.isEditMode}
                  groupsForRights={groupListStore.forRights}
                />
              ),
              disabled: !this.props.isEditMode,
            },
            {
              label: "Options",
              component: (
                <OptionsTab
                  modulesActive={projectSubmitStore.modules}
                  functionsActive={projectSubmitStore.functions}
                  functionsOptions={projectSubmitStore.functionsOptions}
                  modulesOptions={projectSubmitStore.modulesOptions}
                  formRef={this.formRef}
                  isEditView={this.props.isEditMode}
                  groupsForRights={groupListStore.groupsForAccessRights}
                />
              ),
              disabled: !this.props.isEditMode,
            },

            {
              label: t("Members"),
              component: <UsersTab store={this.projectUsersStore} />,
              disabled: !this.props.isEditMode,
            },
            {
              label: t("Information"),
              component: <InformationTab data={projectSubmitStore.infoData} />,
              disabled: !this.props.isEditMode,
            },
          ];
          return subPagesConfig;
        }

        handleTabChange = (activeKey: string) => {
          const { t } = this.props;
          this.formRef.current
            .validateFields()
            .then((values) => {
              projectSubmitStore.updateData(values);
              this.setState({ activeKey });
            })
            .catch(() => {
              message.error(t(`${I18N_NS.GENERAL}:::Edit highlighted fields to continue`));
            });
        };

        setFolderStructure = (folderStructure) => {
          this.setState({ folderStructure });
        };

        handleExcelFileSave = async (id: number) => {
          const { t } = this.props;

          try {
            return await API.newData(API_URL.FOLDERS.createFolders(id), this.state.folderStructure);
          } catch (err) {
            notificationError(t("Error"), t(`${I18N_NS.GENERAL}:::Something technical went wrong.`));
            throw err;
          }
        };

        /**
         * after PAVE-591 refactoring, formRef.current.setFieldsValue
         * got simply ignored, when the form doesn't contain a field with that name.
         * So we pull out the invisible fields from the subforms (f.e. the TimersTab)
         * and put them here to the main/parent form
         */
        renderInvisibleNeededFields() {
          return (
            <Form.Item name="scheduledJobs" style={{ display: "none" }}>
              <div />
            </Form.Item>
          );
        }

        formatBillingDates(values) {
          const dateFields = ["billingStart", "billingEnd", "contractStart", "contractEnd"];
          for (const field of dateFields) {
            if (values.billingInformation[field]) {
              values.billingInformation[field] = +values.billingInformation[field].format("x");
            }
          }
        }

        formatQrFilterValue(values) {
          if (Array.isArray(values?.functionOptions?.plancheckMobileOptions?.qrFilterValue)) {
            values.functionOptions.plancheckMobileOptions.qrFilterValue =
              values.functionOptions.plancheckMobileOptions.qrFilterValue.join(":");
          }
        }

        submitEditProject = async (values) => {
          const { t } = this.props;
          const projectId = +this.props.params.id;

          await this.projectUsersStore.saveUsers(projectSubmitStore.data.id);

          const { scheduledJobs } = values;
          this.formatBillingDates(values);
          this.formatQrFilterValue(values);

          await projectSubmitStore.put(projectId, values as API_AdminProject);

          // Separate API call for editing (PUT) scheduled jobs — PAVE-4766
          if (scheduledJobs) {
            await projectSubmitStore.editScheduledJobs(projectId, scheduledJobs);
          }

          if (!projectSubmitStore.data) return;

          await projectSubmitStore.fetch(projectId);
          notificationSuccess(
            t("Success"),
            t("Edit project {{ name }}", {
              name: projectSubmitStore.data.name,
              interpolation: { escapeValue: false },
            })
          );
        };

        submitNewProject = async (values) => {
          const { t } = this.props;

          await projectSubmitStore.post(values as API_AdminProject);
          if (!projectSubmitStore.data.id) return;

          if (this.state.folderStructure) {
            try {
              await projectSubmitStore.setFolderStructure(projectSubmitStore.data.id, this.state.folderStructure);
            } catch (error) {
              notificationError(
                t("Error"),
                t("Failed to create folder structure for project {{name}}", {
                  name: projectSubmitStore.data.name,
                  interpolation: { escapeValue: false },
                })
              );
            }
          }

          notificationSuccess(
            t("Success"),
            t("New project created {{ name }}", {
              name: projectSubmitStore.data.name,
              interpolation: { escapeValue: false },
            })
          );

          await projectSubmitStore.fetch(projectSubmitStore.data.id);
          if (this.state.continueEditing) {
            this.props.navigate(`/admin/projects/edit/${projectSubmitStore.data.id}`);
          } else {
            this.props.navigate("/admin/projects");
          }
        };

        handleSubmit = async (values) => {
          const { t, isEditMode } = this.props;
          try {
            if (isEditMode) {
              await this.submitEditProject(values);
            } else {
              await this.submitNewProject(values);
            }
          } catch (err) {
            if (err.response?.status === 409 && err.response.data.code === 1066) {
              notificationError(t("Project short name already exists"), t("Please provide another short name"));
            }
            throw err;
          }
        };

        render() {
          const { isEditMode, t } = this.props;
          const { continueEditing } = this.state;

          if (projectSubmitStore.state === "LOADING") {
            return <Loader />;
          }

          const renderTitle = () => {
            return this.props.isEditMode ? (
              <Row justify="space-between">
                <Col>
                  <h1>
                    {t("Edit Project")}{" "}
                    <strong
                      style={{
                        textDecoration: projectSubmitStore.data.status === "ENABLED" ? "" : "line-through",
                        color: projectSubmitStore.data.status === "ENABLED" ? "" : "#ccc",
                      }}
                    >
                      {projectSubmitStore.data.name}
                    </strong>{" "}
                  </h1>
                </Col>
                {/* Function that activate a project */}
                {/* <Col>
                  {projectSubmitStore.data.status === 'DEACTIVATED' && (
                    <Button
                      onClick={() => {
                        projectSubmitStore.updateData({ status: 'ENABLED' });
                      }}
                    >
                      Activate Project
                    </Button>
                  )}
                </Col> */}
                {projectSubmitStore.data.cloneFromPrId ? (
                  <Col>
                    <span style={{ fontSize: 20 }}>
                      {t("Cloned from")}: <strong>{projectSubmitStore.data.cloneFromPrId}</strong>
                    </span>
                  </Col>
                ) : null}
              </Row>
            ) : (
              t("New Project")
            );
          };

          const className = classnames("g-card", {
            disabled: projectSubmitStore.data.status && projectSubmitStore.data.status !== "ENABLED",
          });
          return (
            <>
              <div onClick={this.handleBack} className="detail-action mb-2" style={{ width: 190 }}>
                <span className="detail-action-icon">
                  <FontAwesomeIcon icon={["far", "chevron-left"]} fixedWidth={true} size="sm" />
                </span>
                {this.props.t("Back")}
              </div>
              <div className={className}>
                {renderTitle()}
                <div className="mb-2">
                  <Form
                    onFinish={this.handleSubmit}
                    className="project-config-form"
                    ref={this.formRef}
                    layout="vertical"
                  >
                    {this.renderInvisibleNeededFields()}
                    <Tabs
                      activeKey={this.state.activeKey}
                      onChange={this.handleTabChange}
                      items={this.subPages.map((page) => ({
                        key: page.label,
                        label: t(`${I18N_NS.GENERAL}:::${page.label}`),
                        forceRender: isEditMode,
                        disabled: page.disabled,
                        children: page.component,
                      }))}
                    />
                    <Row justify="space-between" style={{ marginTop: "20px" }}>
                      <Button onClick={() => this.props.navigate("/admin/projects")}>
                        {t(`${I18N_NS.GENERAL}:::Cancel`)}
                      </Button>
                      <div>
                        {!isEditMode && (
                          <Checkbox
                            checked={continueEditing}
                            onChange={() => this.setState({ continueEditing: !continueEditing })}
                          >
                            {t("Continue project creation")}
                          </Checkbox>
                        )}

                        <Button type="primary" htmlType="submit">
                          {this.props.params.id ? t("Save") : t("Create")}
                        </Button>
                      </div>
                    </Row>

                    {!isEditMode && (
                      <div className="footnote">
                        *{" "}
                        {t(
                          `If you uploaded the Excel file to build the folder structure, it may take longer to save the project. Please, wait until the download is complete and do not refresh the page.`
                        )}
                      </div>
                    )}
                  </Form>
                </div>
              </div>
            </>
          );
        }
      }
    )
  )
);
